/**
 * O que faz: Mantém o histórico paginado ordenado e sem mensagens duplicadas.
 * Onde usa: useConversationMessagesInfinite e envio de mensagens.
 * Conexões: TanStack Query.
 * Tabelas: Representação em memória de chat_mensagens.
 * Data: 23/07/2026.
 */
import type { InfiniteData } from '@tanstack/react-query';
import type { ChatMessage, ChatMessagePage } from './chatMessageTypes';

const compararMensagens = (primeira: ChatMessage, segunda: ChatMessage) => {
  const primeiraData = new Date(primeira.created_at).getTime();
  const segundaData = new Date(segunda.created_at).getTime();
  if (primeiraData !== segundaData) return primeiraData - segundaData;
  return primeira.id.localeCompare(segunda.id);
};

const deduplicarOrdenar = (mensagens: ChatMessage[]) => {
  const porId = new Map<string, ChatMessage>();
  mensagens.forEach(mensagem => porId.set(mensagem.id, mensagem));
  return [...porId.values()].sort(compararMensagens);
};

export const flattenMessagesForDisplay = (data?: InfiniteData<ChatMessagePage>) =>
  data
    ? deduplicarOrdenar(data.pages.slice().reverse().flatMap(page => page.messages))
    : [];

export const appendRealtimeMessage = (
  data: InfiniteData<ChatMessagePage>,
  message: ChatMessage,
): InfiniteData<ChatMessagePage> => {
  const pages = [...data.pages];
  const newestPage = pages[0] ?? { messages: [], olderCursor: null };
  pages[0] = {
    ...newestPage,
    messages: deduplicarOrdenar([...newestPage.messages, message]),
  };

  return { ...data, pages };
};

export const mergeRecoveredMessages = (
  data: InfiniteData<ChatMessagePage>,
  messages: ChatMessage[],
) => messages.reduce(appendRealtimeMessage, data);
